import React from 'react';
import { Fence, LayoutDashboard, LeafyGreen} from 'lucide-react';
import '../../css/TutorialPopupStyling.css';
import CollapsibleSection from "../CollapsibleSection";
import DenotedGardenMenuItem from "../DenotedGardenMenuItem";
import VeggieSelector from "../VeggieSelector";
import VeggieInfoPopup from "../VeggieInfoPopup";
import picture1 from '../tutorialComponents/denotingFirstStep.png';
import picture2 from '../tutorialComponents/denotingSecondStep.png';
import picture3 from '../tutorialComponents/denotingThirdStep2.png';

const MainPageTutorialSecond = () => {
    const mockDenotedPlots = {
        "Sunny Patch": [
            [0, 0, 1, 1, 1, 0, 0],
            [0, 1, 1, 1, 1, 1, 0],
            [1, 1, 1, 1, 1, 1, 1],
            [1, 1, 1, 1, 1, 1, 1],
            [1, 1, 1, 1, 1, 1, 1],
            [0, 1, 1, 1, 1, 1, 0],
            [0, 0, 1, 1, 1, 0, 0]

        ]
    };

    const mockGardenCells = `
        0,0,1,1,1,0,0
        0,1,1,1,1,1,0
        1,1,1,1,1,1,1
        1,1,1,1,1,1,1
        1,1,1,1,1,1,1
        0,1,1,1,1,1,0
        0,0,1,1,1,0,0
    `;

    const handleDelete = () => {

    }

    return (
        <div className={"tutorial-first-page-body"}>
            <div className={"tutorial-fp-header"}>
                <p className={"tutorial-fp-header-text"}>
                    Once your garden grid matches your actual layout, use the
                    <span className={`denote-highlight`}> Denote tool </span> to mark your plots and specify their
                    growing conditions.
                </p>
            </div>

            <CollapsibleSection
                title="Denoting Specifications"
                icon={LayoutDashboard}
                iconColor="text-emerald-200"
                defaultExpanded={false}
            >
                <div className={"how-to-use-section"}>
                    <div className={"denoting-picture-tutorial"}>
                        <div className={"denoting-tutorial-picture-container"}>
                            <div className={"denoting-tutorial-picture"}>
                                <img src={picture1} className={"denoting-picture"} alt={"Garden denoting process"}/>
                            </div>
                            <div className={"denoting-tutorial-picture-text-block"}>
                                <div className={"denoting-tutorial-text-enum"}>1</div>
                                <div className={"denoting-tutorial-picture-text-stack"}>
                                    <div className={"denoting-tutorial-text-header"}>
                                        Select Your Plots
                                    </div>
                                    <div className={"denoting-tutorial-text"}>
                                        Click on the plots you'd like to denote. Undenoted plots will appear orange,
                                        while selected ones will turn dark green.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={"denoting-tutorial-picture-container"}>
                            <div className={"denoting-tutorial-picture"}>
                                <img src={picture2} className={"denoting-picture"} alt={"Garden denoting process"}/>
                            </div>
                            <div className={"denoting-tutorial-picture-text-block"}>
                                <div className={"denoting-tutorial-text-enum"}>2</div>
                                <div className={"denoting-tutorial-picture-text-stack"}>
                                    <div className={"denoting-tutorial-text-header"}>
                                        Name Your Garden
                                    </div>
                                    <div className={"denoting-tutorial-text"}>
                                        Give each garden a clear, memorable name—this helps you keep your spaces
                                        organized.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={"denoting-tutorial-picture-container"}>
                            <div className={"denoting-tutorial-picture"}>
                                <img src={picture3} className={"denoting-picture"} alt={"Garden denoting process"}/>
                            </div>
                            <div className={"denoting-tutorial-picture-text-block"}>
                                <div className={"denoting-tutorial-text-enum"}>3</div>
                                <div className={"denoting-tutorial-picture-text-stack"}>
                                    <div className={"denoting-tutorial-text-header"}>
                                        Add Garden Details
                                    </div>
                                    <div className={"denoting-tutorial-text"}>
                                        Add specific information about your garden, such as soil type, hardiness zone,
                                        sunlight levels, and maintenance preferences.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={"denoting-tutorial-text-block-dashed"}>
                            <p>
                                💡 Below is an example of a fully filled-out denoting section for reference.
                            </p>
                        </div>

                        <div className={"denoted-garden-menu-section"}>
                            <DenotedGardenMenuItem
                                key={"Garden"}
                                gardenName={"Garden"}
                                denotedPlots={mockDenotedPlots}
                                gardenCells={mockGardenCells}
                                width={25}
                                height={25}
                                onDelete={handleDelete}
                                onSave={handleDelete}
                                onRotate={handleDelete}
                                onDuplicate={handleDelete}
                                isMainscreen={true}
                            />
                        </div>

                        <div className={"denoting-tutorial-text-block"}>
                            <p>
                                All of this information helps us to tailor planting suggestions and care tips towards
                                your garden’s unique growing conditions. Be sure to save each garden!
                            </p>
                        </div>
                    </div>

                </div>
            </CollapsibleSection>

            <CollapsibleSection
                title="Veggie Selector"
                icon={LeafyGreen}
                iconColor="text-emerald-200"
                defaultExpanded={false}
            >
                <div className={"how-to-use-section2"}>
                    <div className={"veggie-selector-header-section"}>
                        <p>
                            Our Veggie Selector helps you choose the right vegetables for your garden, with detailed
                            growing information and compatibility data to help your plants thrive.
                        </p>
                    </div>

                    <VeggieSelector
                        totalSpace={20}
                        gardenInformation={mockDenotedPlots}
                        veggieFilter={['Red Cherry Tomato', 'Yellow Bell Pepper']}
                    />

                    <div className={"veggie-selector-header-section"}>
                        <p>
                            Add vegetables to your garden plots like items in a shopping cart. We keep track of the
                            space you have, and the plants you intend to grow, but there are no limits to your growing
                            potential.
                        </p>
                    </div>

                    <div className={"denoting-tutorial-text-block-dashed"}>
                        <p>
                            Not sure about a vegetable? Click the info button next to any plant to see detailed
                            growing information and compatibility with your garden conditions.
                        </p>
                    </div>
                </div>

            </CollapsibleSection>

            <div className={"tutorial-fp-header"}>
                <p className={"tutorial-fp-header-text"}>
                    All of the specifications you make feed directly into your personalized planting guide and
                    tracker, helping you stay on top of what to plant, when to plant it, and how to care for
                    each crop throughout the growing season.
                </p>
            </div>
        </div>
    )
}

export default MainPageTutorialSecond;